import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Image as ImageIcon, Edit3, Trash2 } from "lucide-react";
import { api } from "../lib/api";

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const EMPTY = () => ({ id: "", title: "", description: "", discount_label: "", start_date: today(), end_date: today(), link_type: "none", link_value: "", image: "", enabled: true, sort_order: 100 });

const statusOf = (p) => {
  const t = today();
  if (!p.enabled) return ["Off", "text-white/40"];
  if (p.end_date < t) return ["Expired", "text-red-400"];
  if (p.start_date > t) return ["Scheduled", "text-white/60"];
  return ["Live", "text-gold"];
};

function LinkPicker({ form, setForm, products, categories }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <select value={form.link_type} onChange={(e) => setForm({ ...form, link_type: e.target.value, link_value: "" })} data-testid="promo-link-type">
        <option value="none">Shop page</option><option value="product">A product</option><option value="category">A category</option>
      </select>
      {form.link_type === "product" && (
        <select required className="col-span-2" value={form.link_value} onChange={(e) => setForm({ ...form, link_value: e.target.value })} data-testid="promo-link-product">
          <option value="">Choose product…</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      )}
      {form.link_type === "category" && (
        <select required className="col-span-2" value={form.link_value} onChange={(e) => setForm({ ...form, link_value: e.target.value })} data-testid="promo-link-category">
          <option value="">Choose category…</option>
          {categories.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
        </select>
      )}
    </div>
  );
}

export default function AdminPromotions({ headers, products, categories, onCount }) {
  const [list, setList] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => api.get("/admin/promotions", { headers }).then((r) => { setList(r.data); onCount?.(r.data.length); }), [headers, onCount]);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/admin/upload-image", fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, image: `${process.env.REACT_APP_BACKEND_URL}${r.data.url}` }));
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.end_date < form.start_date) { toast.error("End date must be on or after start date"); return; }
    const { id, ...rest } = form;
    const payload = { ...rest, title: rest.title.trim(), sort_order: parseInt(rest.sort_order) || 100 };
    try {
      if (id) await api.put(`/admin/promotions/${id}`, payload, { headers });
      else await api.post("/admin/promotions", payload, { headers });
      toast.success(id ? "Offer updated" : "Offer added");
      setForm(EMPTY()); load();
    } catch (err) { toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Failed to save"); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete offer '${p.title}'?`)) return;
    try {
      await api.delete(`/admin/promotions/${p.id}`, { headers });
      toast.success("Offer deleted");
      if (form.id === p.id) setForm(EMPTY());
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="admin-promotions">
      <form onSubmit={submit} className="glass rounded-2xl p-6 space-y-4 lg:col-span-2 h-fit">
        <div>
          <div className="kicker mb-2">{form.id ? "Edit Offer" : "Add an Offer"}</div>
          <div className="text-xs text-white/50">Shown as a sliding card below the homepage cover between the start and end dates. Hidden when nothing is live.</div>
        </div>
        <input required maxLength={80} placeholder="Title, e.g. Diwali Offer" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full block" data-testid="promo-title" />
        <input maxLength={40} placeholder="Discount label, e.g. Up to 30% off" value={form.discount_label} onChange={(e) => setForm({ ...form, discount_label: e.target.value })} className="w-full block" data-testid="promo-discount" />
        <textarea maxLength={240} rows={2} placeholder="Short description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full block" data-testid="promo-description" />
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-white/50 block">Starts<input required type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1 w-full block" data-testid="promo-start" /></label>
          <label className="text-xs text-white/50 block">Ends<input required type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1 w-full block" data-testid="promo-end" /></label>
        </div>
        <div className="text-xs text-white/50">Button links to</div>
        <LinkPicker form={form} setForm={setForm} products={products} categories={categories} />
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="promo-img-file" />
        {form.image ? (
          <div className="flex items-center gap-3">
            <img src={form.image} alt="" className="w-20 h-14 rounded-lg object-cover" />
            <button type="button" onClick={() => fileRef.current?.click()} className="text-xs text-gold" data-testid="promo-img-replace">Replace</button>
            <button type="button" onClick={() => setForm({ ...form, image: "" })} className="text-xs text-red-400" data-testid="promo-img-remove">Remove</button>
          </div>
        ) : (
          <button type="button" onClick={() => fileRef.current?.click()} className="w-full py-5 rounded-xl border-2 border-dashed border-white/20 hover:border-gold/50 text-xs text-white/60 flex items-center justify-center gap-2" data-testid="promo-img-upload">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4 text-gold" />} Optional image
          </button>
        )}
        <div className="grid grid-cols-2 gap-3 items-center">
          <label className="text-xs text-white/50 block">Order<input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="mt-1 w-full block" data-testid="promo-order" /></label>
          <label className="flex items-center gap-2 text-sm mt-4"><input type="checkbox" className="!w-4 !h-4" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="promo-enabled" /> Enabled</label>
        </div>
        <div className="flex gap-2">
          <button type="submit" className="btn-gold flex-1 justify-center" data-testid="promo-save">{form.id ? "Save changes" : "Add offer"}</button>
          {form.id && <button type="button" onClick={() => setForm(EMPTY())} className="btn-ghost-outline" data-testid="promo-cancel">Cancel</button>}
        </div>
      </form>
      <div className="lg:col-span-3 space-y-3" data-testid="promo-list">
        {list === null && <div className="glass rounded-2xl p-8 text-sm text-white/50 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading offers…</div>}
        {list?.length === 0 && <div className="glass rounded-2xl p-8 text-sm text-white/50">No offers yet.</div>}
        {(list || []).map((p) => {
          const [label, cls] = statusOf(p);
          return (
            <div key={p.id} className="glass rounded-2xl p-4 flex items-center gap-4" data-testid={`promo-row-${p.id}`}>
              {p.image ? <img src={p.image} alt="" className="w-16 h-12 rounded-lg object-cover" /> : <div className="w-16 h-12 rounded-lg bg-white/5" />}
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{p.title} {p.discount_label && <span className="text-gold text-sm">· {p.discount_label}</span>}</div>
                <div className="text-xs text-white/50">{p.start_date} → {p.end_date} · <span className={cls} data-testid={`promo-status-${p.id}`}>{label}</span></div>
              </div>
              <button onClick={() => setForm({ ...EMPTY(), ...p })} className="p-2 hover:text-gold" data-testid={`promo-edit-${p.id}`}><Edit3 className="w-4 h-4" /></button>
              <button onClick={() => del(p)} className="p-2 hover:text-red-400" data-testid={`promo-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
